import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';

import { LabeledField } from '@/__core/components/LabeledField';
import AddressField from '@/modules/addresses/components/AddressField';

export interface ItemSupplierAddPayload {
  name: string;
  email?: string;
  phone?: string;
  address?: {
    address_line?: string;
    province?: string;
    ward?: string;
    free_address?: string;
  };
}

interface ItemSupplierAddModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ItemSupplierAddPayload) => Promise<any>;
  loading?: boolean;
}

interface SupplierFormData {
  name: string;
  email: string;
  phone: string;
  address: {
    address_line?: string;
    province?: string;
    ward?: string;
    free_address?: string;
  };
}

const createInitialState = (): SupplierFormData => ({
  name: '',
  email: '',
  phone: '',
  address: {
    address_line: '',
    province: '',
    ward: '',
    free_address: '',
  },
});

export default function ItemSupplierAddModal({ open, onClose, onSave, loading = false }: ItemSupplierAddModalProps) {
  const [formData, setFormData] = useState<SupplierFormData>(createInitialState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const busy = loading || submitting;

  const handleFieldChange = (field: 'name' | 'email' | 'phone', value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const resetState = useCallback(() => {
    setFormData(createInitialState());
    setError(null);
    setFieldErrors({});
  }, []);

  const handleClose = useCallback(() => {
    if (busy) return;
    resetState();
    onClose();
  }, [busy, onClose, resetState]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      nextErrors.name = 'Tên nhà cung cấp là bắt buộc';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      nextErrors.email = 'Email không hợp lệ';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = useCallback(async () => {
    if (!validate()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload: ItemSupplierAddPayload = {
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        address: formData.address,
      };

      await onSave(payload);
      resetState();
      onClose();
    } catch (saveError: any) {
      setError(saveError?.message || 'Có lỗi xảy ra khi tạo nhà cung cấp');
    } finally {
      setSubmitting(false);
    }
  }, [formData, onClose, onSave, resetState]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid #e5e7eb',
          pb: 2,
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#111827',
        }}
      >
        Thêm nhà cung cấp
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{  mt: 2 }}>
            <LabeledField label='Tên nhà cung cấp *' labelWidth={140} error={!!fieldErrors.name} helperText={fieldErrors.name}>
              <TextField
                fullWidth
                value={formData.name}
                onChange={(event) => handleFieldChange('name', event.target.value)}
                error={!!fieldErrors.name}
                disabled={busy}
                placeholder='Nhập tên nhà cung cấp'
              />
            </LabeledField>
        </Box>
        <Grid container rowSpacing={0} columnSpacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Số điện thoại' labelWidth={140}>
              <TextField
                fullWidth
                value={formData.phone}
                onChange={(event) => handleFieldChange('phone', event.target.value)}
                disabled={busy}
                placeholder='Nhập số điện thoại'
              />
            </LabeledField>
            </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Email' labelWidth={140} error={!!fieldErrors.email} helperText={fieldErrors.email}>
              <TextField
                fullWidth
                type='email'
                value={formData.email}
                onChange={(event) => handleFieldChange('email', event.target.value)}
                error={!!fieldErrors.email}
                disabled={busy}
                placeholder='Nhập email'
              />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <AddressField
              value={formData.address}
              styleDisplay='small'
              onChange={(address) => {
                setFormData((prev) => ({ ...prev, address }));
              }}
              isEditMode
              disabled={busy}
            />
          </Grid>
        </Grid>

        {error ? (
          <Box sx={{ color: '#ef4444', fontSize: '0.75rem', mt: 1 }}>{error}</Box>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: '1px solid #e5e7eb',
          p: 2,
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          variant='outlined'
          disabled={busy}
          sx={{
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Hủy
        </Button>
        <Button
          onClick={() => {
            void handleSave();
          }}
          variant='contained'
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: '#10b981',
            '&:hover': {
              backgroundColor: '#059669',
            },
            '&:disabled': {
              backgroundColor: '#d1d5db',
              color: '#9ca3af',
            },
          }}
        >
          {busy ? 'Đang tạo...' : 'Tạo nhà cung cấp'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
